/**
 * BulkProductImport - Simplified bulk product import from CSV/Excel
 * Phase 6: Admin Catalog Management Enhancement
 */
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { 
  Loader2, 
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronRight
} from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Required and optional columns for import
const REQUIRED_COLUMNS = ['name', 'category_id'];
const OPTIONAL_COLUMNS = ['sku', 'description', 'pricing_type', 'retail_price', 'cost_price', 'lead_time_days', 'image_url'];
const ALL_COLUMNS = [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS];

const BulkProductImport = ({ open, onOpenChange, categories = [], onImportComplete }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Results
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [options, setOptions] = useState({
    update_existing: false,
    skip_errors: true
  });
  
  const fileInputRef = useRef(null);

  const resetState = () => {
    setStep(1);
    setFile(null);
    setParsedData([]);
    setValidationErrors([]);
    setImportResults(null);
  };

  const handleClose = () => {
    resetState();
    onOpenChange(false);
  };

  // Generate template Excel file
  const downloadTemplate = () => {
    const templateData = [
      {
        name: 'Example Product 1',
        category_id: categories[0]?.id || 'paste-category-id-here',
        sku: '', // Leave empty for auto-generate
        description: 'Product description here',
        pricing_type: 'fixed',
        retail_price: 29.99,
        cost_price: 15.00,
        lead_time_days: 7,
        image_url: ''
      },
      {
        name: 'Example Product 2',
        category_id: categories[0]?.id || 'paste-category-id-here',
        sku: 'CUSTOM-SKU-001',
        description: 'Another product',
        pricing_type: 'fixed',
        retail_price: 49.99,
        cost_price: 25.00,
        lead_time_days: 5,
        image_url: 'https://example.com/image.jpg'
      }
    ];

    // Create category reference sheet
    const categoryRef = categories.map(c => ({
      category_id: c.id,
      name: c.name,
      product_type: c.product_type,
      default_pricing_type: c.pricing_type
    }));

    const wb = XLSX.utils.book_new();
    
    // Products sheet
    const ws1 = XLSX.utils.json_to_sheet(templateData);
    XLSX.utils.book_append_sheet(wb, ws1, 'Products');
    
    // Categories reference sheet
    const ws2 = XLSX.utils.json_to_sheet(categoryRef);
    XLSX.utils.book_append_sheet(wb, ws2, 'Category Reference');
    
    XLSX.writeFile(wb, 'product_import_template.xlsx');
    toast.success('Template downloaded');
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;
    
    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    const isValid = validTypes.includes(uploadedFile.type) || 
                   uploadedFile.name.endsWith('.xlsx') || 
                   uploadedFile.name.endsWith('.csv');
    
    if (!isValid) {
      toast.error('Please upload an Excel (.xlsx) or CSV file');
      return;
    }
    
    setFile(uploadedFile);
    setLoading(true);
    
    try {
      const data = await uploadedFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      
      if (jsonData.length === 0) {
        toast.error('No data found in file');
        setLoading(false);
        return;
      }
      
      // Validate columns
      const firstRow = jsonData[0];
      const columns = Object.keys(firstRow);
      const missingRequired = REQUIRED_COLUMNS.filter(col => !columns.includes(col));
      
      if (missingRequired.length > 0) {
        toast.error(`Missing required columns: ${missingRequired.join(', ')}`);
        setLoading(false);
        return;
      }
      
      // Validate each row
      const errors = [];
      const validatedData = jsonData.map((row, idx) => {
        const rowErrors = [];
        
        if (!row.name || row.name.toString().trim() === '') {
          rowErrors.push('name is required');
        }
        if (!row.category_id || row.category_id.toString().trim() === '') {
          rowErrors.push('category_id is required');
        } else {
          // Check if category exists
          const cat = categories.find(c => c.id === row.category_id);
          if (!cat) {
            rowErrors.push('invalid category_id');
          }
        }
        
        if (row.retail_price && isNaN(parseFloat(row.retail_price))) {
          rowErrors.push('retail_price must be a number');
        }
        
        if (rowErrors.length > 0) {
          errors.push({ row: idx + 2, errors: rowErrors }); // +2 for header row and 0-index
        }
        
        return {
          ...row,
          _rowNum: idx + 2,
          _valid: rowErrors.length === 0,
          _errors: rowErrors
        };
      });
      
      setParsedData(validatedData);
      setValidationErrors(errors);
      setStep(2);
      
    } catch (error) {
      console.error('Parse error:', error);
      toast.error('Failed to parse file. Please check the format.');
    }
    
    setLoading(false);
  };

  // Run the import
  const handleImport = async () => {
    const validProducts = parsedData.filter(p => p._valid).map(p => ({
      name: p.name,
      category_id: p.category_id,
      sku: p.sku || undefined,
      description: p.description || undefined,
      pricing_type: p.pricing_type || undefined,
      retail_price: p.retail_price ? parseFloat(p.retail_price) : undefined,
      cost_price: p.cost_price ? parseFloat(p.cost_price) : undefined,
      lead_time_days: p.lead_time_days ? parseInt(p.lead_time_days) : undefined,
      image_url: p.image_url || undefined
    }));
    
    if (validProducts.length === 0) {
      toast.error('No valid products to import');
      return;
    }
    
    setImporting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/catalog/products/bulk-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          products: validProducts,
          options
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setImportResults(data.results);
        setStep(3);
        toast.success(data.message);
        onImportComplete?.();
      } else {
        toast.error(data.detail || 'Import failed');
      }
    } catch (error) {
      toast.error('Error during import');
      console.error(error);
    }
    
    setImporting(false);
  };

  // Get category name by ID
  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : categoryId;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-import-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <FileSpreadsheet className="w-5 h-5" />
            Bulk Product Import
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Upload an Excel or CSV file to import multiple products'}
            {step === 2 && `Preview: ${parsedData.filter(p => p._valid).length} valid products to import`}
            {step === 3 && 'Import complete'}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Download Template */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-blue-900">Need a template?</p>
                    <p className="text-sm text-blue-700">Download our Excel template with all columns and a category reference</p>
                  </div>
                  <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Upload Area */}
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              {loading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
                  <span className="text-gray-600">Processing file...</span>
                </div>
              ) : file ? (
                <div className="flex flex-col items-center gap-2">
                  <FileSpreadsheet className="w-10 h-10 text-green-500" />
                  <span className="font-medium">{file.name}</span>
                  <span className="text-sm text-gray-500">Click to change file</span>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600 font-medium mb-1">Drop your file here or click to browse</p>
                  <p className="text-sm text-gray-400">Supports .xlsx and .csv files</p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="file-upload-input"
              />
            </div>

            {/* Required Columns Info */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">Required Columns:</p>
              <div className="flex flex-wrap gap-2">
                {REQUIRED_COLUMNS.map(col => (
                  <Badge key={col} variant="default">{col}</Badge>
                ))}
              </div>
              <p className="text-sm font-medium text-gray-700 mt-3 mb-2">Optional Columns:</p>
              <div className="flex flex-wrap gap-2">
                {OPTIONAL_COLUMNS.map(col => (
                  <Badge key={col} variant="outline">{col}</Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 2 && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{parsedData.filter(p => p._valid).length}</p>
                  <p className="text-sm text-gray-500">Valid Products</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">{parsedData.filter(p => !p._valid).length}</p>
                  <p className="text-sm text-gray-500">With Errors</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-gray-600">{parsedData.length}</p>
                  <p className="text-sm text-gray-500">Total Rows</p>
                </CardContent>
              </Card>
            </div>

            {/* Error List */}
            {validationErrors.length > 0 && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <p className="font-medium text-red-900">Validation Errors</p>
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {validationErrors.slice(0, 10).map((err, idx) => (
                      <p key={idx} className="text-sm text-red-700">
                        Row {err.row}: {err.errors.join(', ')}
                      </p>
                    ))}
                    {validationErrors.length > 10 && (
                      <p className="text-sm text-red-500 italic">...and {validationErrors.length - 10} more errors</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Preview Table */}
            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-left">Category</th>
                      <th className="px-3 py-2 text-left">Price</th>
                      <th className="px-3 py-2 text-left">SKU</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.slice(0, 20).map((row, idx) => (
                      <tr key={idx} className={row._valid ? '' : 'bg-red-50'}>
                        <td className="px-3 py-2">
                          {row._valid ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                        </td>
                        <td className="px-3 py-2 font-medium">{row.name}</td>
                        <td className="px-3 py-2 text-gray-600">{getCategoryName(row.category_id)}</td>
                        <td className="px-3 py-2">${row.retail_price || '0.00'}</td>
                        <td className="px-3 py-2 font-mono text-xs">{row.sku || '(auto)'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {parsedData.length > 20 && (
                <div className="px-3 py-2 bg-gray-50 text-sm text-gray-500">
                  Showing first 20 of {parsedData.length} rows
                </div>
              )}
            </div>

            {/* Import Options */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <p className="font-medium">Import Options</p>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="update_existing"
                    checked={options.update_existing}
                    onCheckedChange={(v) => setOptions(prev => ({ ...prev, update_existing: v }))}
                  />
                  <Label htmlFor="update_existing" className="text-sm">
                    Update existing products with matching SKU
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="skip_errors"
                    checked={options.skip_errors}
                    onCheckedChange={(v) => setOptions(prev => ({ ...prev, skip_errors: v }))}
                  />
                  <Label htmlFor="skip_errors" className="text-sm">
                    Skip rows with errors and continue import
                  </Label>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => { setStep(1); setFile(null); }}>
                Back
              </Button>
              <Button 
                onClick={handleImport} 
                disabled={importing || parsedData.filter(p => p._valid).length === 0}
                data-testid="run-import-btn"
              >
                {importing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
                ) : (
                  <>Import {parsedData.filter(p => p._valid).length} Products <ChevronRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Results */}
        {step === 3 && importResults && (
          <div className="space-y-6">
            {/* Results Summary */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4 text-center">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-600" />
                  <p className="text-2xl font-bold text-green-700">{importResults.created}</p>
                  <p className="text-sm text-green-600">Created</p>
                </CardContent>
              </Card>
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4 text-center">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                  <p className="text-2xl font-bold text-blue-700">{importResults.updated}</p>
                  <p className="text-sm text-blue-600">Updated</p>
                </CardContent>
              </Card>
              <Card className="border-gray-200 bg-gray-50">
                <CardContent className="p-4 text-center">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-gray-500" />
                  <p className="text-2xl font-bold text-gray-700">{importResults.skipped}</p>
                  <p className="text-sm text-gray-500">Skipped</p>
                </CardContent>
              </Card>
            </div>

            {/* Errors if any */}
            {importResults.errors && importResults.errors.length > 0 && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <p className="font-medium text-red-900 mb-2">Import Errors</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {importResults.errors.map((err, idx) => (
                      <p key={idx} className="text-sm text-red-700">
                        Row {err.row}: {err.error}
                      </p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => { resetState(); }}>
                Import More
              </Button>
              <Button onClick={handleClose} data-testid="close-import-btn">
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BulkProductImport;
